import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { addDays, toIsoDate } from "@/lib/financial/calc";
import { periodPresets } from "./helpers";
import type { Period } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import { useFinancialFormat } from "./useFinancialFormat";

export interface PeriodState extends Period {
  presetKey: string | null;
}

interface CalendarGridProps {
  month: string;
  onMonth: (m: string) => void;
  isSelected: (iso: string) => boolean;
  isInside: (iso: string) => boolean;
  today: string;
  onPick: (iso: string) => void;
}

function CalendarGrid({ month, onMonth, isSelected, isInside, today, onPick }: CalendarGridProps) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const year = Number(month.slice(0, 4));
  const mon = Number(month.slice(5, 7)) - 1;
  const first = new Date(year, mon, 1);
  const last = new Date(year, mon + 1, 0);
  const shift = (n: number) => {
    const d = new Date(year, mon + n, 1);
    onMonth(toIsoDate(d).slice(0, 7));
  };
  const initials = t("dow.initials", { returnObjects: true }) as string[];

  return (
    <div className="w-full sm:w-[320px]">
      <div className="flex items-center justify-between">
        <button type="button" aria-label={t("period.prevMonth")} onClick={() => shift(-1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground/70 hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <div className="font-display text-sm font-bold capitalize">{t("period.monthOfYear", { month: fmt.monthLong(mon), year })}</div>
        <button type="button" aria-label={t("period.nextMonth")} onClick={() => shift(1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground/70 hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-0.5" role="grid">
        {initials.map((d, i) => (
          <div key={i} className="pb-1 text-center text-[11px] font-semibold text-muted-foreground/70" aria-hidden>{d}</div>
        ))}
        {Array.from({ length: first.getDay() }, (_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: last.getDate() }, (_, i) => {
          const iso = toIsoDate(new Date(year, mon, i + 1));
          const sel = isSelected(iso);
          const inside = isInside(iso);
          const isToday = iso === today;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onPick(iso)}
              aria-pressed={sel}
              aria-label={fmt.dnum(iso)}
              className={cn(
                "h-9 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                sel ? "bg-primary font-bold text-primary-foreground" : inside ? "bg-primary/10" : isToday ? "bg-muted font-bold" : "hover:bg-muted",
              )}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TriggerButton({ label, sub }: { label: string; sub: string }) {
  const { t } = useTranslation("financial");
  return (
    <PopoverTrigger asChild>
      <button type="button" className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-xl border border-border bg-card px-3.5 py-2 text-left hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <CalendarDays className="h-4 w-4 text-primary" aria-hidden />
        <span className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">{sub} · {t("period.clickToChange")}</span>
        </span>
      </button>
    </PopoverTrigger>
  );
}

function PresetList({ items }: { items: { key: string; label: string; active: boolean; onClick: () => void }[] }) {
  const { t } = useTranslation("financial");
  return (
    <div className="flex flex-row gap-1 overflow-x-auto border-b border-border bg-muted/40 p-2 sm:w-48 sm:flex-col sm:border-b-0 sm:border-r sm:p-3">
      <div className="hidden px-2 pb-2 font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block">{t("period.shortcuts")}</div>
      {items.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={p.onClick}
          className={cn(
            "whitespace-nowrap rounded-lg px-2.5 py-2 text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            p.active ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-muted",
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function RangePicker({ value, onChange, today }: { value: PeriodState; onChange: (p: PeriodState) => void; today: string }) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<"from" | "to">("from");
  const [month, setMonth] = useState(value.from.slice(0, 7));

  const pick = (iso: string) => {
    if (phase === "from") {
      onChange({ from: iso, to: iso, presetKey: null });
      setPhase("to");
      return;
    }
    if (iso < value.from) onChange({ from: iso, to: value.to, presetKey: null });
    else onChange({ from: value.from, to: iso, presetKey: null });
    setPhase("from");
    setOpen(false);
  };

  const label = t("period.range", { from: fmt.dshort(value.from), to: fmt.dshort(value.to), year: value.to.slice(0, 4) });
  const sub = value.presetKey ? t(`period.presets.${value.presetKey}`) : t("period.custom");

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); setPhase("from"); if (o) setMonth(value.from.slice(0, 7)); }}>
      <TriggerButton label={label} sub={sub} />
      <PopoverContent align="end" className="w-[calc(100vw-2rem)] overflow-hidden rounded-2xl p-0 sm:w-auto">
        <div className="flex flex-col sm:flex-row">
          <PresetList
            items={periodPresets(today).map((p) => ({
              key: p.key,
              label: t(`period.presets.${p.key}`),
              active: value.from === p.from && value.to === p.to,
              onClick: () => { onChange({ from: p.from, to: p.to, presetKey: p.key }); setOpen(false); },
            }))}
          />
          <div className="p-4">
            <p className="mb-3 text-sm font-semibold text-foreground">{phase === "from" ? t("period.pickStart") : t("period.pickEnd")}</p>
            <CalendarGrid
              month={month}
              onMonth={setMonth}
              today={today}
              isSelected={(iso) => iso === value.from || iso === value.to}
              isInside={(iso) => iso > value.from && iso < value.to}
              onPick={pick}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DayPicker({ value, onChange, today }: { value: string; onChange: (iso: string) => void; today: string }) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(value.slice(0, 7));
  const presets = [
    { key: "today", iso: today },
    { key: "yesterday", iso: addDays(today, -1) },
    { key: "twoDaysAgo", iso: addDays(today, -2) },
  ];
  const choose = (iso: string) => { onChange(iso); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setMonth(value.slice(0, 7)); }}>
      <TriggerButton label={fmt.dlong(value)} sub={value === today ? t("period.today") : t("period.otherDay")} />
      <PopoverContent align="end" className="w-[calc(100vw-2rem)] overflow-hidden rounded-2xl p-0 sm:w-auto">
        <div className="flex flex-col sm:flex-row">
          <PresetList items={presets.map((p) => ({ key: p.key, label: t(`period.presets.${p.key}`), active: value === p.iso, onClick: () => choose(p.iso) }))} />
          <div className="p-4">
            <p className="mb-3 text-sm font-semibold text-foreground">{t("period.pickDay")}</p>
            <CalendarGrid month={month} onMonth={setMonth} today={today} isSelected={(iso) => iso === value} isInside={() => false} onPick={choose} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
